
import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../context/UserContext"
import "../../scss/main.scss"
import SearchTerms from "../../widget/SearchTerms";
import FunctionBanner from "../FunctionBanner";
import CampaignDialog from "./dialogs/CampaignDialog";
import CampaignInviteDialog from "./dialogs/CampaignInviteDialog";
import { form } from './dialogs/CampaignForm';
import CampaignDeleteCandidateDataDialog from "./dialogs/CampaignDeleteCandidateDataDialog";
import CampaignTable from "./CampaignTable";
import { Button, IconButton, Tooltip } from "@mui/material";
import EventAvailableTwoToneIcon from '@mui/icons-material/EventAvailableTwoTone';
import EventRepeatTwoToneIcon from '@mui/icons-material/EventRepeatTwoTone';
import EditCalendarTwoToneIcon from '@mui/icons-material/EditCalendarTwoTone';
import PersonRemoveTwoToneIcon from '@mui/icons-material/PersonRemoveTwoTone';
import CampaignTwoToneIcon from '@mui/icons-material/CampaignTwoTone';
import ApiClient from "../../api/ApiClient";
import PromiseBuilder from "../../utils/PromiseUtils";
import { useNotifications } from "../../hooks/notification/notificationHook";
import BlurryBackdrop from "../../widget/BlurryBackdrop";
import { campaignsDtoToJ, usersDtoToJ } from '../../utils/CampaignMapper';

const CampaignPage = (props) => {

  const { user } = useContext(UserContext);
  const { t } = useTranslation();
  const { addErrorNotification } = useNotifications();

  const [countries, setCountries] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [managers, setManagers] = useState([]);
  const [searchItems, setSearchItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Array());
  const [containerDialogOpen, setContainerDialogOpen] = useState(false);
  const [containerDialogForm, setContainerDialogForm] = useState(form);
  const [containerInviteDialogOpen, setContainerInviteDialogOpen] = useState(false);
  const [containerDeleteCandidateDataDialogOpen, setContainerDeleteCandidateDataDialogOpen] = useState(false);
  const [showActiveCampaigns, setShowActiveCampaigns] = useState(true);
  const [showInactiveCampaigns, setShowInactiveCampaigns] = useState(false);

  useEffect(() => {
    fetchCountriesData();
  }, []);

  useEffect(() => {
    fetchCampaignData();
    if (user.isAdmin) {
      fetchManagersData();
    }
  }, [countries]);

  const fetchCountriesData = () => {
    setIsLoading(true);
    new PromiseBuilder()
      .then(async () =>
        ApiClient.get("/api/v1/country")
      )
      .then(async (res) => {
        setCountries(res.data.countries);
        setIsLoading(false);
      })
      .catch(async (e) => {
        addErrorNotification(t("toast.fetchCountriesFailed"), t("toast.error"));
        console.log("ERROR", e);
        setCountries([]);
        setIsLoading(false);
      })
      .run();
  }

  const fetchCampaignData = () => {
    setIsLoading(true);
    new PromiseBuilder()
      .then(async () => ApiClient.get("/api/v1/campaigns"))
      .then(async (res) => {
        setIsLoading(false);
        const campaigns = campaignsDtoToJ(res.data.campaigns);
        setCampaigns(campaigns);
      })
      .catch(async (e) => {
        setIsLoading(false);
        addErrorNotification(t("toast.fetchCampaignsFailed"), t("toast.error"));
        setCampaigns([]);
        console.log("ERROR", e);
      })
      .run();
  }

  const fetchManagersData = () => {
    setIsLoading(true);
    new PromiseBuilder()
      .then(async () => ApiClient.get("/api/v1/user/manager/" + null))
      .then(async (res) => {
        setIsLoading(false);
        const managers = usersDtoToJ(res.data.users);
        setManagers(managers);
      })
      .catch(async (e) => {
        setIsLoading(false);
        addErrorNotification(t("toast.fetchManagersFailed"), t("toast.error"));
        setManagers([]);
        console.log("ERROR", e);
      })
      .run();
  }

  const uncheckAll = () => {
    setSelectedItems(new Array());
  }

  const openCampaignDialog = (id) => {
    if (id != null) {
      const item = campaigns.find((item) =>
        item.id == id
      );
      const newForm = {
        id: id,
        campaign: item.campaign,
        company: item.company,
        country: countries.find((c) => c.code == item.country.code),
        managers: item.managers.map(e => managers.find(m => m.id === e.id)).filter(m => m !== undefined),
        recruiters: item.recruiters
      }
      setContainerDialogForm(newForm);
    } else {
      setContainerDialogForm(form);
    }
    setContainerDialogOpen(true);
  }

  const closeCampaignDialog = () => {
    setContainerDialogOpen(false);
  }

  const saveCampaign = (campaign, resetForm) => {
    setIsLoading(true);
    new PromiseBuilder()
      .then(async () =>
        campaign.id ? ApiClient.put("/api/v1/campaign", campaign) : ApiClient.post("/api/v1/campaign", campaign)
      )
      .then(async (res) => {
        fetchCampaignData();
        resetForm();
        setIsLoading(false);
        closeCampaignDialog(true);
      })
      .catch(async (e) => {
        setIsLoading(false);
        addErrorNotification(t("toast.savingCampaignFailed"), t("toast.error"));
        console.log("ERROR", e);
      })
      .run();

    setContainerDialogOpen(false);
  }

  const openCampaignInviteDialog = () => {
    setContainerInviteDialogOpen(true);
  }

  const closeCampaignInviteDialog = (uncheck) => {
    setContainerInviteDialogOpen(false);
    if (uncheck) {
      uncheckAll();
    }
  }

  const openCampaignDeleteCandidateDataDialog = () => {
    setContainerDeleteCandidateDataDialogOpen(true);
  }

  const closeCampaignDeleteCandidateDataDialog = () => {
    setContainerDeleteCandidateDataDialogOpen(false);
  }

  const toggleShowActiveCampaigns = () => {
    setShowActiveCampaigns(!showActiveCampaigns);
  }

  const toggleShowInactiveCampaigns = () => {
    setShowInactiveCampaigns(!showInactiveCampaigns);
  }

  return (
    <>
      <BlurryBackdrop
        open={isLoading}
      />

      {user.isAdmin && (<>
        <CampaignDialog
          open={containerDialogOpen}
          handleClose={closeCampaignDialog}
          form={containerDialogForm}
          handleSave={saveCampaign}
          managers={managers}
          countries={countries}
        />

        <CampaignInviteDialog
          open={containerInviteDialogOpen}
          handleClose={closeCampaignInviteDialog}
          selectedCampaigns={selectedItems}
          setIsLoading={setIsLoading}
        />

        <CampaignDeleteCandidateDataDialog
          open={containerDeleteCandidateDataDialogOpen}
          handleClose={closeCampaignDeleteCandidateDataDialog}
          setIsLoading={setIsLoading}
        />
      </>)}
      <FunctionBanner>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
          <SearchTerms searchTerms={searchItems} setSearchTerms={setSearchItems} style={{ widht: '100%' }} />
          <Tooltip title={t('campaign.toggleActiveCampaigns')}>
            <Button
              variant="outlined"
              size="small"
              className={"kanban is-rating-button is-rating-button-a" + (showActiveCampaigns ? "-active" : "")}
              style={{ fontWeight: '800', boxShadow: 'none', borderRadius: '10px' }}
              onClick={() => {
                toggleShowActiveCampaigns();
              }}
            >
              <EventRepeatTwoToneIcon />
            </Button>
          </Tooltip>
          <Tooltip title={t('campaign.toggleInactiveCampaigns')}>
            <Button
              variant="outlined"
              size="small"
              className={"kanban is-rating-button is-rating-button-b" + (showInactiveCampaigns ? "-active" : "")}
              style={{ fontWeight: '800', boxShadow: 'none', borderRadius: '10px' }}
              onClick={() => {
                toggleShowInactiveCampaigns();
              }}
            >
              <EventAvailableTwoToneIcon />
            </Button>
          </Tooltip>
        </div>
        {user.isAdmin && (
          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'end', margin: '20px' }}>
            <Tooltip title={t("candidates.deleteData")}>
              <IconButton
                size="large"
                onClick={() => openCampaignDeleteCandidateDataDialog()}
                style={{ marginRight: '5px' }}>

                <PersonRemoveTwoToneIcon
                  fontSize="large"
                  className="color color-medium-red"
                />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("campaign.create")}>
              <IconButton
                size="large"
                onClick={() => openCampaignDialog(null)}
                style={{ marginRight: '5px', borderRadius: '10px' }}>
                <EditCalendarTwoToneIcon
                  fontSize="large"
                  className="color color-medium-pink"
                />
              </IconButton>
            </Tooltip>
            <Tooltip title={t("campaign.inviteToCampaign")}>
              <span>
                <IconButton
                  size="large"
                  disabled={selectedItems.length == 0}
                  onClick={() => openCampaignInviteDialog()}>
                  <CampaignTwoToneIcon
                    fontSize="large"
                    disabled={selectedItems.length == 0}
                    className={"color color-" + (selectedItems.length == 0 ? "light-gray" : 'medium-purple')}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )}
      </FunctionBanner>

      <div style={{ width: '100%', marginTop: '120px', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <CampaignTable
          campaigns={campaigns}
          selected={selectedItems}
          setSelected={setSelectedItems}
          openCampaignDialog={openCampaignDialog}
          searchTerms={searchItems}
          showActive={showActiveCampaigns}
          showInactive={showInactiveCampaigns}
          isUserAdmin={user.isAdmin}
        />
      </div>
    </>
  );
}

export default CampaignPage;